import {isNil} from 'lodash';
import {SchemeLink} from '../../../applink/SchemeLink';
import {colorToRgba} from '../../../common/colorToRgba';
import {isNilEmpty} from '../../../common/shared/utils/isNilEmpty';
import {twMerge} from '../../../common/twMerge';
import {useExecuteInView} from '../../../common/useExecuteInView.hook';
import {BadgeType} from '../../../graphql/generated/types';
import {BADGE_TYPE_TO_ICON_TYPE_MAP} from '../../atoms/Icon/constants';
import {Icon} from '../../atoms/Icon/Icon';
import {Image} from '../../atoms/Image/Image';
import {Ranking} from '../../atoms/Ranking/Ranking';
import {RankingType} from '../../atoms/Ranking/RankingType.enum';
import {SeparatorType} from '../../atoms/Separator/SeparatorType.enum';
import {StaticImage} from '../../atoms/StaticImage/StaticImage';
import {StaticImageType} from '../../atoms/StaticImage/StaticImageType.enum';
import {Text} from '../../atoms/Text/Text';
import {BadgesWithSpace} from '../../molecules/BadgesWithSpace/BadgesWithSpace';
import {LabelBadge} from '../../molecules/LabelBadge/LabelBadge';
import {LabelBadge16Type} from '../../molecules/LabelBadge/LabelBadge16Type.enum';
import {LabelBadgeSizeType} from '../../molecules/LabelBadge/LabelBadgeSizeType.enum';
import {ThumbnailRatio} from '../../molecules/ThumbnailWithBadges/ThumbnailRatio.constant';
import {ThumbnailType} from '../../molecules/ThumbnailWithBadges/ThumbnailType.enum';
import {Metadata} from '../Metadata/Metadata';
import {CardViewItemProps} from './CardViewItemProps.interface';

export function CardViewItem({
  thumbnail,
  badgeList,
  ageGradeBadge,
  statusBadge,
  subtitleList,
  scheme,
  isAgeRestricted,
  categoryType,
  imageLazyLoad,
  rank,
  rankVariation,
  altText,
  className,
  onInView,
  discountRateText,
  cardViewTitle,
  backgroundColor: _backgroundColor,
  isBook,
  isLegacy,
  isEmpty,
  ...props
}: CardViewItemProps) {
  const ref = useExecuteInView({callback: onInView});

  const isTitleWithoutSubtitle =
    !isNilEmpty(cardViewTitle) && isNilEmpty(subtitleList) && !rank;

  const mergedBadgeList: BadgeType[] = [
    ...(badgeList ? badgeList : []),
    ...(statusBadge ? [statusBadge] : []),
  ];

  const backgroundColor =
    isNil(_backgroundColor) || !/^#[0-9A-F]{6}$/i.test(_backgroundColor)
      ? '#333333' // TODO: @Root-huh 컬러 토큰 값 수정되면 rgb(var(--...)) 으로 수정하기
      : _backgroundColor;

  return (
    <SchemeLink scheme={scheme || undefined}>
      <div
        ref={ref}
        className={twMerge(
          'relative @container/cardview',
          THUMBNAIL_STYLE,
          className
        )}
        aria-label={altText || ''}
        {...props}
      >
        {isEmpty ? (
          <div
            className={THUMBNAIL_STYLE}
            style={{
              aspectRatio: `1 / ${ThumbnailRatio[ThumbnailType.Card]}`,
            }}
          >
            <StaticImage
              imageType={StaticImageType.ThumbnailEmptyCardStatic}
              className="object-cover absolute top-0 left-0 w-full h-full"
              aria-hidden={true}
            />
          </div>
        ) : (
          <>
            <div className="relative">
              {isAgeRestricted && (
                <div
                  className={THUMBNAIL_STYLE}
                  style={{
                    aspectRatio: `1 / ${ThumbnailRatio[ThumbnailType.Card]}`,
                  }}
                >
                  <StaticImage
                    imageType={StaticImageType.Thumbnail19CardStatic}
                    className="object-cover object-bottom absolute inset-0 w-full h-full"
                    aria-hidden={true}
                  />
                </div>
              )}
              {!isAgeRestricted && (
                <Image
                  src={thumbnail}
                  alt="썸네일"
                  className={THUMBNAIL_STYLE}
                  ratio={ThumbnailRatio[ThumbnailType.Card]}
                  isLazy={imageLazyLoad}
                  aria-hidden={true}
                />
              )}
              {isBook && (
                <div
                  /**
                   * 서브픽셀 렌더링 + GPU 합성 오차로 인해 블러 영역이 살짝 어긋나 뒤에 위치한 썸네일 이미지가 블러 밖으로 노출되는 케이스가 발생
                   * 이를 방지하기 위해 0.5px 만큼 추가 영역을 차지 설정 (실제 영역보다 넘치는 부분은 overflow-hidden에 의해 숨김처리)
                   */
                  className={twMerge(
                    'absolute -inset-[0.5px] bg-[rgb(var(--colors-static-mono-black)_/_20%)] backdrop-blur-[50px] use-gpu'
                  )}
                  style={{
                    WebkitBackdropFilter: 'blur(50px)',
                  }}
                />
              )}
            </div>

            {/* 메인 이미지 영역 */}
            {isBook && (
              <div className="absolute inset-0 pt-[12px] px-[16px]">
                {/* 메인 이미지 */}
                {isAgeRestricted && (
                  <div
                    className="w-full shadow-[8px_10px_24px_1px_rgba(0,0,0,0.4)]"
                    style={{
                      aspectRatio: `1 / ${ThumbnailRatio[ThumbnailType.Book]}`,
                    }}
                  >
                    <StaticImage
                      imageType={StaticImageType.Thumbnail19PosterStatic}
                      className={twMerge(
                        THUMBNAIL_STYLE,
                        'object-cover h-full rounded-6pxr'
                      )}
                      aria-hidden={true}
                    />
                  </div>
                )}
                {!isAgeRestricted && (
                  <Image
                    src={thumbnail}
                    alt="썸네일"
                    className={twMerge(
                      THUMBNAIL_STYLE,
                      'rounded-6pxr shadow-[8px_10px_24px_1px_rgba(0,0,0,0.4)]'
                    )}
                    ratio={ThumbnailRatio[ThumbnailType.Book]}
                    isLazy={imageLazyLoad}
                    aria-hidden={true}
                  />
                )}
              </div>
            )}

            {!isAgeRestricted && !isBook && !isLegacy && (
              <div
                className="absolute bottom-0 w-full h-1/2"
                style={{
                  background: `linear-gradient(180deg, 
                ${colorToRgba(backgroundColor, 0)} 0%,
                ${colorToRgba(backgroundColor, 0.6)} 30%, 
                ${colorToRgba(backgroundColor, 0.9)} 100%)`,
                }}
              />
            )}

            {/* 정보 영역 */}
            <div
              className={twMerge(
                'absolute inset-0 flex flex-col items-center justify-end px-8pxr pb-8pxr w-full @container pt-[10%]'
              )}
            >
              <Text
                className={twMerge(
                  'text-center text-static-mono-white text-ellipsis font-medium2-alt-bold @[0px]/cardview:font-small1-alt-bold @[111px]/cardview:font-medium2-alt-bold @[140px]/cardview:font-medium1-alt-bold',
                  'break-words supports-[overflow-wrap:anywhere]:keep-word-anywhere',
                  isBook
                    ? isTitleWithoutSubtitle
                      ? 'line-clamp-3'
                      : 'line-clamp-2'
                    : isTitleWithoutSubtitle
                      ? 'line-clamp-4'
                      : 'line-clamp-3'
                )}
              >
                {cardViewTitle}
              </Text>
              {!isAgeRestricted &&
                !rank &&
                subtitleList &&
                subtitleList.length > 0 && (
                  <Metadata
                    texts={subtitleList}
                    separatorType={SeparatorType.Dot}
                    useStaticDot={true}
                    subtitleClassName="text-static-mono-white opacity-80 font-x-small2"
                    className="justify-center mx-4pxr mt-2pxr"
                    aria-hidden={true}
                  />
                )}
              {rank && (
                <Ranking
                  type={RankingType.Static}
                  rank={rank}
                  rankVariation={rankVariation}
                  className="mx-auto mt-6pxr"
                  aria-hidden={true}
                />
              )}
            </div>

            <div className="flex absolute left-4pxr top-4pxr">
              {!isAgeRestricted && discountRateText && (
                <LabelBadge
                  sizeType={LabelBadgeSizeType.Size16}
                  type={LabelBadge16Type.red}
                  label={discountRateText}
                  className="mr-2pxr"
                />
              )}
              {!isAgeRestricted &&
                mergedBadgeList &&
                mergedBadgeList.length > 0 && (
                  <BadgesWithSpace badgeList={mergedBadgeList} />
                )}
            </div>

            {!isAgeRestricted && ageGradeBadge && (
              <div className="absolute right-4pxr top-4pxr">
                <Icon type={BADGE_TYPE_TO_ICON_TYPE_MAP[ageGradeBadge]} />
              </div>
            )}
          </>
        )}
      </div>
    </SchemeLink>
  );
}

const THUMBNAIL_STYLE = 'rounded-8pxr overflow-hidden w-full';
